import { useState } from "react";
import { motion } from "framer-motion";
import { auth, apiPost } from "../api.js";
import { t } from "../i18n.js";
import { BrainHero } from "../components/BrainHero.js";
import type { LoginResponse } from "@dfa/types";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await apiPost<LoginResponse>("api/auth/login", { email, password });
      auth.setSession(res.access_token, res.user);
      window.location.assign("/");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg.includes("401") ? "Email atau password salah" : msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen overflow-hidden bg-gradient-to-br from-surface-1 via-white to-brand-50 relative">
      {/* Decorative soft blobs (no animation — purely visual depth). */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-brand-200/40 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[28rem] h-[28rem] rounded-full bg-brand-100/40 blur-3xl" />
      </div>

      <main className="relative min-h-screen grid md:grid-cols-2 max-w-6xl w-full mx-auto px-6 sm:px-10 py-8 gap-8 items-center">
        {/* Left — brand hero. */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="flex flex-col items-center md:items-start text-center md:text-left order-1"
        >
          <BrainHero size={96} className="mb-8" />
          <motion.h1
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-4xl sm:text-5xl font-semibold tracking-tight text-brand-900 leading-tight"
          >
            Dermatoglyphics<br />
            <span className="text-brand-500">Fingerprint Analysis</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.32 }}
            className="mt-4 text-base sm:text-lg text-brand-700/80 max-w-md"
          >
            Membaca potensi alami anak melalui sidik jari — disusun oleh DCPPI selama 20 tahun.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-6 hidden md:flex items-center gap-3 text-xs text-brand-700/70"
          >
            <span className="px-2 py-1 rounded-full bg-white/60 border border-brand-200">AI + Geometri Algoritmik</span>
            <span className="px-2 py-1 rounded-full bg-white/60 border border-brand-200">Audit Log Immutable</span>
            <span className="px-2 py-1 rounded-full bg-white/60 border border-brand-200">Psikolog Gerbang Akhir</span>
          </motion.div>
        </motion.section>

        {/* Right — login card. */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut", delay: 0.15 }}
          className="order-2 w-full max-w-md mx-auto md:ml-auto"
        >
          <div className="bg-white/85 backdrop-blur-lg border border-surface-3 rounded-2xl shadow-depth-3 p-7">
            <div className="mb-5">
              <h2 className="text-xl font-semibold text-brand-900">Masuk</h2>
              <p className="text-sm text-slate-500 mt-1">Akses untuk psikolog & agen terlatih.</p>
            </div>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-xs font-medium text-slate-700 mb-1">
                  {t.email}
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  placeholder="bu.emmy@dfa.local"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-xs font-medium text-slate-700 mb-1">
                  {t.password}
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input"
                />
              </div>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18 }}
                  className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                >
                  {error}
                </motion.div>
              )}
              <button type="submit" disabled={loading} className="btn-primary w-full">
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    {t.loading}
                  </span>
                ) : (
                  t.login
                )}
              </button>
            </form>
            <p className="text-[11px] text-slate-400 mt-4 text-center">
              DCPPI — Dermatoglyphics Consulting Peak Performance Indonesia
            </p>
          </div>
        </motion.section>
      </main>
    </div>
  );
}
